import { useEffect, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import TokenUploadCropModal from "./TokenUploadCropModal";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { PawPrint, Plus } from "lucide-react";

// A player's "master" token library — every token image they've ever uploaded (kind=token),
// across ALL their campaigns, so re-adding a character/companion to a map (or bringing the same
// look into a different table) never forces a re-upload. New art is always still one tap away.
export default function MyTokensPanel({ gameId, scene, myMember, hasOwnPcToken, character, onNeedCharacter, autoOpenTrigger }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingType, setPendingType] = useState("pc");
  const [library, setLibrary] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);

  useEffect(() => {
    if (autoOpenTrigger) setMenuOpen(true);
  }, [autoOpenTrigger]);

  useEffect(() => {
    if (!menuOpen) return;
    setLoadingLibrary(true);
    api.get("/uploads/library", { params: { kind: "token" } })
      .then((r) => setLibrary(r.data))
      .catch(() => setLibrary([]))
      .finally(() => setLoadingLibrary(false));
  }, [menuOpen]);

  const pcLibrary = library.filter((a) => (a.tag || "pc") === "pc");
  const companionLibrary = library.filter((a) => a.tag === "companion");

  const openUpload = (type) => {
    setPendingType(type);
    setMenuOpen(false);
    setUploadOpen(true);
  };

  const placeToken = async (type, { url, name }) => {
    if (!scene) return;
    await api.post(`/games/${gameId}/tokens`, {
      scene_id: scene.id,
      name,
      image_url: url,
      x: (scene.width || 1600) / 2 + Math.random() * 80 - 40,
      y: (scene.height || 1000) / 2 + Math.random() * 80 - 40,
      owner_member_id: myMember?.id,
      token_type: type,
    });
    setMenuOpen(false);
  };

  const renderLibrarySection = (type, items, label, addLabel) => (
    <div className="space-y-2">
      <p className="text-[10px] uppercase text-[#f1e0c5]/40">{label}</p>
      {items.length > 0 ? (
        <div className="grid grid-cols-4 gap-2">
          {items.map((a) => (
            <button
              key={a.id}
              data-testid={`reuse-token-${a.id}`}
              onClick={() => placeToken(type, { url: fileUrl(a.id), name: a.original_filename?.split(".")[0] || "Token" })}
              className="flex flex-col items-center gap-1 group"
              title={a.original_filename}
            >
              <img src={fileUrl(a.id)} alt={a.original_filename} className="w-14 h-14 rounded-full object-cover border-2 border-white/10 group-hover:border-[#D4AF37] transition-colors duration-200" />
              <span className="text-[9px] text-[#f1e0c5]/50 truncate w-14 text-center">{a.original_filename?.split(".")[0]}</span>
            </button>
          ))}
          <button
            data-testid={`new-${type}-token-button`}
            onClick={() => openUpload(type)}
            className="flex flex-col items-center gap-1 justify-center"
          >
            <span className="w-14 h-14 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center hover:border-[#D4AF37] transition-colors duration-200">
              <Plus className="w-5 h-5 text-[#f1e0c5]/40" />
            </span>
            <span className="text-[9px] text-[#f1e0c5]/50">New Token</span>
          </button>
        </div>
      ) : (
        <Button data-testid={type === "pc" ? "add-my-pc-token-button" : "add-companion-token-button"} onClick={() => openUpload(type)} className={`w-full rounded-full ${type === "pc" ? "bg-[#D90429] hover:bg-[#b8031f]" : ""}`} variant={type === "pc" ? "default" : "secondary"}>
          {addLabel}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <button
        data-testid="my-tokens-fab"
        onClick={() => setMenuOpen(true)}
        className="absolute bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 md:left-auto md:right-[420px] md:translate-x-0 z-20 px-4 h-11 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 flex items-center gap-2 hover:border-[#D4AF37] transition-colors duration-200 text-sm"
      >
        <PawPrint className="w-4 h-4 text-[#D4AF37]" /> My Tokens
      </button>

      <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
        <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm" data-testid="my-tokens-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Your Tokens</DialogTitle>
            <DialogDescription>Reuse a token you've made before (across any of your tables), or add a new one.</DialogDescription>
          </DialogHeader>
          {loadingLibrary ? (
            <p className="text-xs text-[#f1e0c5]/40 text-center py-4">Loading your tokens…</p>
          ) : (
            <div className="space-y-4">
              {character ? (
                renderLibrarySection("pc", pcLibrary, "Your Characters", hasOwnPcToken ? "Add Another Character Token" : "Add My Character Token")
              ) : (
                <div className="space-y-2 text-center bg-black/20 border border-white/10 rounded-xl p-3">
                  <p className="text-[10px] uppercase text-[#f1e0c5]/40">Your Characters</p>
                  <p className="text-xs text-[#f1e0c5]/60">Create your character sheet first — your token gets placed on the map right after, so it's always tied to the right sheet.</p>
                  <Button
                    data-testid="my-tokens-create-character-first-button"
                    onClick={() => { setMenuOpen(false); onNeedCharacter?.(); }}
                    className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]"
                  >
                    Create Your Character
                  </Button>
                </div>
              )}
              {renderLibrarySection("companion", companionLibrary, "Your Companions", "Add a Companion")}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TokenUploadCropModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        gameId={gameId}
        tag={pendingType}
        onUploaded={(payload) => placeToken(pendingType, payload)}
      />
    </>
  );
}
